/**
 * ProtectedRoute Component
 * Handles authentication and role-based access control
 */

import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import { Box, CircularProgress } from "@mui/material";
import { ServerUnreachable } from "./ServerUnreachable";
import type { PermissionValue } from "@/constants";
import { homePathForRole } from "@/utils/homePath";
import { isAfterSignIn } from "@/utils/navigation";

interface ProtectedRouteProps {
  children: React.ReactElement;
  roles?: string[];
  permission?: PermissionValue;
  /**
   * Where a signed-in person who may not open this page goes: the
   * Unauthorized page (default), or straight to their own home — for pages
   * like /dashboard that some roles reach by habit or bookmark.
   */
  deniedRedirect?: "unauthorized" | "home";
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  roles,
  permission,
  deniedRedirect = "unauthorized",
}) => {
  const location = useLocation();
  // Select individual slices instead of the whole store, so the guard (and its
  // wrapped page subtree) only re-renders on the auth fields it actually uses —
  // not on every unrelated store mutation. (hasRole/hasPermission are stable
  // action refs.)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const hasRole = useAuthStore((s) => s.hasRole);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const isHydrated = useAuthStore((s) => s.isHydrated);
  const serverUnreachable = useAuthStore((s) => s.serverUnreachable);

  // Wait for auth store rehydration before making redirect decisions
  if (!isHydrated) {
    return <AuthLoading />;
  }

  if (serverUnreachable) {
    return <ServerUnreachable />;
  }

  // Check if user is authenticated
  if (!isAuthenticated || !user) {
    // Redirect to login page with return URL
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Force password change if required
  if (user.must_change_password && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }

  // A home that is this very page would redirect forever; Unauthorized
  // is the safe floor. Straight after sign-in, a refused return path (a
  // stale or shared link) goes home too: LoginPage leaves the decision to
  // this guard rather than keeping its own per-role list of pages.
  const home = homePathForRole(user.role);
  const toHome = deniedRedirect === "home" || isAfterSignIn(location.state);
  const deniedTo = toHome && home !== location.pathname ? home : "/unauthorized";

  // Check role-based access
  if (roles && !hasRole(roles)) {
    return <Navigate to={deniedTo} replace />;
  }

  // Check permission-based access
  if (permission && !hasPermission(permission)) {
    return <Navigate to={deniedTo} replace />;
  }

  // User is authenticated and authorized
  return children;
};

/**
 * Loading component for authentication checks
 */
export const AuthLoading: React.FC = () => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        bgcolor: "background.default",
      }}
    >
      <CircularProgress size={60} />
    </Box>
  );
};
